import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  SetUserMFAPreferenceCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  ChangePasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';

const REGION = process.env.REACT_APP_COGNITO_REGION || 'us-east-1';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';

const client = new CognitoIdentityProviderClient({ region: REGION });

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface MFASetupResponse {
  secretCode: string;
  qrCodeUrl: string;
  session: string;
}

export interface AuthResponse {
  tokens?: AuthTokens;
  challengeName?: string;
  session?: string;
  challengeParameters?: Record<string, string>;
}

class AuthService {
  /**
   * Sign in with username and password
   */
  async signIn(username: string, password: string): Promise<AuthResponse> {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      const response = await client.send(command);

      // Check if MFA is required
      if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        return {
          challengeName: 'SOFTWARE_TOKEN_MFA',
          session: response.Session,
        };
      }

      // Check if new password is required (first-time login)
      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: response.Session,
          challengeParameters: response.ChallengeParameters,
        };
      }

      // Check if MFA setup is required
      if (response.ChallengeName === 'MFA_SETUP') {
        return {
          challengeName: 'MFA_SETUP',
          session: response.Session,
        };
      }

      // Successful authentication
      if (response.AuthenticationResult) {
        return {
          tokens: {
            accessToken: response.AuthenticationResult.AccessToken!,
            idToken: response.AuthenticationResult.IdToken!,
            refreshToken: response.AuthenticationResult.RefreshToken!,
            expiresIn: response.AuthenticationResult.ExpiresIn!,
          },
        };
      }

      throw new Error('Unexpected authentication response');
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.message || 'Authentication failed');
    }
  }

  /**
   * Respond to NEW_PASSWORD_REQUIRED challenge
   */
  async completeNewPasswordChallenge(
    username: string,
    newPassword: string,
    session: string
  ): Promise<AuthResponse> {
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
      });

      const response = await client.send(command);

      // After setting new password, check for MFA_SETUP challenge
      if (response.ChallengeName === 'MFA_SETUP') {
        return {
          challengeName: 'MFA_SETUP',
          session: response.Session,
        };
      }

      // If we get tokens back, authentication is complete
      if (response.AuthenticationResult) {
        return {
          tokens: {
            accessToken: response.AuthenticationResult.AccessToken!,
            idToken: response.AuthenticationResult.IdToken!,
            refreshToken: response.AuthenticationResult.RefreshToken!,
            expiresIn: response.AuthenticationResult.ExpiresIn!,
          },
        };
      }

      throw new Error('Unexpected response after password change');
    } catch (error: any) {
      console.error('New password challenge error:', error);
      throw new Error(error.message || 'Failed to set new password');
    }
  }

  /**
   * Setup MFA - Get QR code for Google Authenticator
   * Can be called with session (during MFA_SETUP challenge) or access token (user-initiated)
   */
  async setupMFA(sessionOrToken?: string, useSession: boolean = false, username?: string): Promise<MFASetupResponse> {
    try {
      const command = useSession 
        ? new AssociateSoftwareTokenCommand({ Session: sessionOrToken })
        : new AssociateSoftwareTokenCommand({ AccessToken: sessionOrToken });

      const response = await client.send(command);
      const secretCode = response.SecretCode!;

      // Generate QR code URL for Google Authenticator
      const appName = 'AlliumHealth';
      const accountName = username || 'user';
      const qrCodeUrl = `otpauth://totp/${appName}:${accountName}?secret=${secretCode}&issuer=${appName}`;

      return {
        secretCode,
        qrCodeUrl,
        session: response.Session || sessionOrToken || '',
      };
    } catch (error: any) {
      console.error('MFA setup error:', error);
      throw new Error(error.message || 'Failed to setup MFA');
    }
  }

  /**
   * Verify MFA code during setup and complete authentication
   * This handles the MFA_SETUP challenge response
   */
  async verifyMFASetupAndAuthenticate(
    username: string,
    code: string,
    session: string,
    friendlyDeviceName: string = 'My Device'
  ): Promise<AuthTokens> {
    try {
      // First, verify the software token
      const verifyCommand = new VerifySoftwareTokenCommand({
        Session: session,
        UserCode: code,
        FriendlyDeviceName: friendlyDeviceName,
      });

      const verifyResponse = await client.send(verifyCommand);

      // After verification, respond to the MFA_SETUP challenge
      const respondCommand = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: 'MFA_SETUP',
        Session: verifyResponse.Session,
        ChallengeResponses: {
          USERNAME: username,
        },
      });

      const authResponse = await client.send(respondCommand);

      if (authResponse.AuthenticationResult) {
        return {
          accessToken: authResponse.AuthenticationResult.AccessToken!,
          idToken: authResponse.AuthenticationResult.IdToken!,
          refreshToken: authResponse.AuthenticationResult.RefreshToken!,
          expiresIn: authResponse.AuthenticationResult.ExpiresIn!,
        };
      }

      throw new Error('MFA setup completed but no tokens returned');
    } catch (error: any) {
      console.error('MFA verification error:', error);
      throw new Error(error.message || 'Failed to verify MFA code');
    }
  }

  /**
   * Verify MFA code during setup (with access token - for user-initiated MFA setup)
   */
  async verifyMFASetup(accessToken: string, code: string, friendlyDeviceName: string = 'My Device'): Promise<void> {
    try {
      // Verify the token
      const verifyCommand = new VerifySoftwareTokenCommand({
        AccessToken: accessToken,
        UserCode: code,
        FriendlyDeviceName: friendlyDeviceName,
      });

      await client.send(verifyCommand);

      // Enable MFA for the user
      const setMFACommand = new SetUserMFAPreferenceCommand({
        AccessToken: accessToken,
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: true,
        },
      });

      await client.send(setMFACommand);
    } catch (error: any) {
      console.error('MFA verification error:', error);
      throw new Error(error.message || 'Failed to verify MFA code');
    }
  }

  /**
   * Respond to MFA challenge during sign-in
   */
  async respondToMFAChallenge(
    username: string,
    code: string,
    session: string
  ): Promise<AuthTokens> {
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: 'SOFTWARE_TOKEN_MFA',
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          SOFTWARE_TOKEN_MFA_CODE: code,
        },
      });

      const response = await client.send(command);

      if (response.AuthenticationResult) {
        return {
          accessToken: response.AuthenticationResult.AccessToken!,
          idToken: response.AuthenticationResult.IdToken!,
          refreshToken: response.AuthenticationResult.RefreshToken!,
          expiresIn: response.AuthenticationResult.ExpiresIn!,
        };
      }

      throw new Error('MFA verification failed');
    } catch (error: any) {
      console.error('MFA challenge error:', error);
      throw new Error(error.message || 'Invalid MFA code');
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      });

      const response = await client.send(command);

      if (response.AuthenticationResult) {
        return {
          accessToken: response.AuthenticationResult.AccessToken!,
          idToken: response.AuthenticationResult.IdToken!,
          refreshToken: refreshToken,
          expiresIn: response.AuthenticationResult.ExpiresIn!,
        };
      }

      throw new Error('Token refresh failed');
    } catch (error: any) {
      console.error('Token refresh error:', error);
      throw new Error(error.message || 'Failed to refresh token');
    }
  }

  /**
   * Sign out user globally
   */
  async signOut(accessToken: string): Promise<void> {
    try {
      const command = new GlobalSignOutCommand({
        AccessToken: accessToken,
      });

      await client.send(command);
    } catch (error: any) {
      console.error('Sign out error:', error);
    }
  }

  /**
   * Get user information
   */
  async getUserInfo(accessToken: string): Promise<any> {
    try {
      const command = new GetUserCommand({
        AccessToken: accessToken,
      });

      const response = await client.send(command);
      return {
        username: response.Username,
        attributes: response.UserAttributes?.reduce((acc: Record<string, string>, attr: any) => {
          acc[attr.Name] = attr.Value;
          return acc;
        }, {} as Record<string, string>),
        mfaEnabled: response.UserMFASettingList?.includes('SOFTWARE_TOKEN_MFA'),
      };
    } catch (error: any) {
      console.error('Get user info error:', error);
      throw new Error(error.message || 'Failed to get user info');
    }
  }

  /**
   * Change password
   */
  async changePassword(
    accessToken: string,
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      const command = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword,
      });

      await client.send(command);
    } catch (error: any) {
      console.error('Change password error:', error);
      throw new Error(error.message || 'Failed to change password');
    }
  }

  /**
   * Decode JWT token (client-side only for display purposes)
   */
  decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
